import React, { useState, useEffect, useRef } from 'react';
import { X, Check, Minus, Share2, Phone, Mail, Lock, Flag, UserPlus } from 'react-feather';
import { XIcon } from './XIcon';
import { rosterNeed } from '../utils/rosterNeed';
import RosterNeedBand from './RosterNeedBand';
import { schoolsAPI, ComprehensiveSchool } from '../services/schoolsAPI';
import { integrationsAPI } from '../services/integrationsAPI';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../hooks/useResponsive';
import { userAPI, familyAPI, coachUnlocksAPI, schoolSlug } from '../services/api';
import CoachTweetsSection from './CoachTweetsSection';
import CoachContactModal, { ContactChannel, ContactCoach } from './CoachContactModal';
import CoachReportModal, { ReportCoach } from './CoachReportModal';
import type { CoachReportKind } from '../services/api';
import Modal from './Modal';
import ErrorState from './ErrorState';
import { useSubscriptionGuard } from '../hooks/useSubscriptionGuard';
import { swrGet, swrSet } from '../utils/swrCache';

interface SchoolProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  schoolId: number | null;
  staffMode?: boolean;
  // When the parent already knows the athlete (Dashboard / College Lists do),
  // pass it to skip the modal's own getProfile+getMyFamily resolution pair.
  athleteProfileId?: string;
  /** Open on this tab instead of Overview (the game plan opens on coaching). */
  initialTab?: TabType;
}

export type TabType = 'overview' | 'academics' | 'coaching' | 'athletics' | 'roster';

// Session-scoped caches so reopening a school is instant (stale-while-
// revalidate: cached data paints, the fetch still runs and replaces it).
const COMPREHENSIVE_TTL_MS = 5 * 60 * 1000;
// DM eligibility per athlete+handle — avoids the per-coach request fan-out on
// every open. 10-min TTL so a mid-session X connect still surfaces quickly.
const canSendDMCache = new Map<string, { v: boolean; t: number }>();
const CAN_SEND_DM_TTL_MS = 10 * 60 * 1000;

const SchoolProfileModal: React.FC<SchoolProfileModalProps> = ({ isOpen, onClose, schoolId, staffMode = false, athleteProfileId, initialTab }) => {
  const { sessionToken } = useAuth();
  // §1.5: coaches unlocked this session (production_id -> revealed contact) so
  // the card + composer reveal immediately; and which coach is mid-unlock.
  const [unlockedContacts, setUnlockedContacts] = useState<Record<string, any>>({});
  const [unlockingCoachId, setUnlockingCoachId] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  // §1.5 free-unlock status ("N of 3 left"); null/isPaid → no counter shown.
  const [unlockStatus, setUnlockStatus] = useState<{ isPaid?: boolean; remaining?: number; limit?: number } | null>(null);
  // §1.5: coach pending an explicit unlock confirm (the "Unlock coach" flow).
  const [pendingUnlockCoach, setPendingUnlockCoach] = useState<any>(null);
  // APS-260: the coach change report that is open, if any.
  const [reportTarget, setReportTarget] = useState<{
    coach: ReportCoach | null;
    coaches?: ReportCoach[];
    kind?: CoachReportKind;
  } | null>(null);

  useEffect(() => {
    if (!isOpen || !sessionToken || staffMode) return;
    coachUnlocksAPI
      .getStatus(sessionToken, athleteProfileId || undefined)
      .then(setUnlockStatus)
      .catch(() => {});
  }, [isOpen, sessionToken, staffMode, athleteProfileId]);

  // §1.5 free-athlete flow: contacting a locked coach via ANY channel
  // (email / phone / tweet / DM) spends a free unlock (up to 3), reveals ALL of
  // that coach's contact, then performs the action. At the limit -> paywall.
  // Paid coaches already carry their contact fields, so onRevealed fires at once.
  const revealedOf = (coach: any) =>
    unlockedContacts[coach.production_id != null ? String(coach.production_id) : ''];
  const phoneOf = (coach: any) => coach.phone || (revealedOf(coach) && revealedOf(coach).phone) || '';
  const twitterOf = (coach: any) =>
    coach.twitter_handle || (revealedOf(coach) && revealedOf(coach).twitter_handle) || '';
  // A coach is LOCKED for this viewer when they're a free (non-paid) athlete who
  // hasn't revealed this coach yet — the backend redacted the name + every
  // channel (has_* flags true, values null). Such a coach shows a single
  // "Unlock coach" CTA instead of the channel buttons. Staff/paid never locked.
  const isCoachLocked = (coach: any) =>
    !staffMode && !unlockStatus?.isPaid && !revealedOf(coach) &&
    !coach.email && !coach.phone && !coach.twitter_handle;
  // What the report form may know about a coach. A locked coach's email and
  // phone are never passed, so the form cannot show them.
  const toReportCoach = (coach: any): ReportCoach => {
    const locked = isCoachLocked(coach);
    const revealed = revealedOf(coach);
    return {
      production_id: Number(coach.production_id),
      coach_name: coach.coach_name,
      position: coach.position,
      title: coach.title,
      email: locked ? null : coach.email || (revealed && revealed.email) || null,
      phone: locked ? null : phoneOf(coach) || null,
    };
  };
  const reportLinkStyle: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '8px 4px', background: 'transparent', border: 'none',
    fontSize: '13px', fontWeight: 600, color: 'var(--aps-text-2)',
    textDecoration: 'underline', textUnderlineOffset: '3px',
    cursor: 'pointer', fontFamily: 'Manrope, sans-serif',
  };

  const unlockCoachThen = async (coach: any, onRevealed: (c: any) => void) => {
    const coachId = coach.production_id != null ? String(coach.production_id) : '';
    // Re-entrancy guard: a double-tap while an unlock is in flight must not spend
    // a second free unlock for the same coach.
    if (coachId && unlockingCoachId === coachId) return;
    // Already revealed: paid coach (contact fields present) or unlocked this session.
    const already = revealedOf(coach) || (coach.email || coach.phone || coach.twitter_handle ? coach : null);
    if (already) { onRevealed(already); return; }
    if (!sessionToken || !coachId) { requirePaid(`Contact ${coach.coach_name || 'Coach'}`, () => {}); return; }
    setUnlockingCoachId(coachId);
    try {
      const res = await coachUnlocksAPI.unlock(
        sessionToken, coachId, schoolId?.toString(), athleteProfileId || undefined,
      );
      if (res.status === 402 || res.paywall) {
        requirePaid(`Contact ${coach.coach_name || 'Coach'}`, () => {});
      } else if ((res.coaches && res.coaches.length) || res.coach) {
        // A PROGRAM unlock reveals the whole coaching staff — merge every coach
        // the server returned, not just the one that was tapped.
        const revealed = (res.coaches && res.coaches.length) ? res.coaches : [res.coach];
        setUnlockedContacts((prev) => {
          const next = { ...prev };
          for (const c of revealed) {
            const pid = c && c.production_id != null ? String(c.production_id) : null;
            if (pid) next[pid] = c;
          }
          return next;
        });
        if (typeof res.remaining === 'number') {
          setUnlockStatus((prev) => (prev ? { ...prev, remaining: res.remaining as number } : prev));
        }
        onRevealed(res.coach || revealed[0] || coach);
      } else {
        requirePaid(`Contact ${coach.coach_name || 'Coach'}`, () => {});
      }
    } catch {
      requirePaid(`Contact ${coach.coach_name || 'Coach'}`, () => {});
    } finally {
      setUnlockingCoachId(null);
    }
  };

  // §1.5 explicit unlock: the "Unlock coach" CTA. At the limit → paywall; else
  // open the confirm. Confirming spends ONE of the 3 free unlocks and reveals
  // ALL of that coach's channels at once (per-coach, not per-channel).
  const handleUnlockClick = (coach: any) => {
    const remaining = unlockStatus?.remaining ?? (unlockStatus?.limit ?? 3);
    if (!unlockStatus?.isPaid && remaining <= 0) {
      requirePaid(`Contact ${coach.coach_name || 'this coach'}`, () => {});
      return;
    }
    setPendingUnlockCoach(coach);
  };
  const confirmUnlock = () => {
    const coach = pendingUnlockCoach;
    setPendingUnlockCoach(null);
    if (coach) unlockCoachThen(coach, () => {}); // reveal all channels, no compose action
  };

  const handleEmailCoach = (coach: any) => {
    unlockCoachThen(coach, (c) => {
      setSelectedEmailCoach({
        name: c.coach_name || coach.coach_name,
        email: c.email,
        title: c.title || coach.title,
        school: school?.name,
        coach_id: coach.production_id != null ? String(coach.production_id) : '',
        school_id: schoolId?.toString(),
      });
      setIsEmailModalVisible(true);
    });
  };
  const { requirePaid } = useSubscriptionGuard();
  const { isMobile, isTablet } = useResponsive();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const contentScrollRef = useRef<HTMLDivElement>(null);
  // Reset scroll to top when switching tabs so a new tab doesn't inherit the
  // previous tab's scroll offset. The actual scroll container can be either this
  // div OR the shared-Modal dialog box (its overflowY:auto vs our contentStyle
  // overflow:hidden resolve ambiguously), so reset this element AND any scrolled
  // ancestor. Re-run on the next frame + a short timeout to beat async tab content
  // and scroll-anchoring re-applying the old offset.
  useEffect(() => {
    const el = contentScrollRef.current;
    if (!el) return;
    const reset = () => {
      el.scrollTop = 0;
      let p: HTMLElement | null = el.parentElement;
      while (p && p !== document.body) {
        if (p.scrollTop) p.scrollTop = 0;
        p = p.parentElement;
      }
    };
    reset();
    const raf = requestAnimationFrame(reset);
    const t = setTimeout(reset, 120);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [activeTab]);
  const [school, setSchool] = useState<ComprehensiveSchool | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [athleteId, setAthleteId] = useState<string>('');

  // Tweet Coach Modal state
  const [isTweetModalVisible, setIsTweetModalVisible] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState<{
    name: string;
    handle: string;
    coach_id?: string;
    school_id?: string;
  } | null>(null);

  // Email Coach Modal state
  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
  const [selectedEmailCoach, setSelectedEmailCoach] = useState<{
    name: string;
    email: string;
    title?: string;
    school?: string;
    coach_id?: string;
    school_id?: string;
  } | null>(null);

  // DM Coach Modal state
  const [isDMModalVisible, setIsDMModalVisible] = useState(false);
  const [selectedDMCoach, setSelectedDMCoach] = useState<{
    name: string;
    twitter_handle: string;
    coach_id?: string;
    school_id?: string;
  } | null>(null);

  // Pre-check which coaches can receive DMs
  const [canSendDMMap, setCanSendDMMap] = useState<Record<string, boolean>>({});

  // Load athlete ID on mount (skip in staff mode). When the parent passed
  // athleteProfileId we use it directly — no network. The getProfile+getMyFamily
  // fallback below only remains for call sites that don't know the athlete.
  useEffect(() => {
    if (staffMode) return;
    // undefined = call site doesn't know the athlete (use fallback);
    // '' = parent is still resolving (wait — it will re-render with the id).
    if (athleteProfileId !== undefined) {
      if (athleteProfileId) setAthleteId(athleteProfileId);
      return;
    }
    const loadAthleteId = async () => {
      if (!sessionToken) return;

      try {
        const userProfile = await userAPI.getProfile(sessionToken);
        const familyResult = await familyAPI.getMyFamily(sessionToken);

        if (familyResult.family?.members) {
          const familyMember = familyResult.family.members.find(
            member => member.id === userProfile.id
          );

          if (familyMember?.athlete_profile_id) {
            setAthleteId(familyMember.athlete_profile_id);
          }
        }
      } catch (error) {
        console.error('Error loading athlete ID:', error);
      }
    };

    loadAthleteId();
  }, [sessionToken, athleteProfileId, staffMode]);

  // Load school data when modal opens
  useEffect(() => {
    if (isOpen && schoolId && sessionToken) {
      setActiveTab(initialTab || 'overview');
      setError(null);
      setCanSendDMMap({});
      loadSchoolData();
    } else if (!isOpen) {
      setSchool(null);
      setError(null);
      setCanSendDMMap({});
    }
    setReportTarget(null);
  }, [isOpen, schoolId, sessionToken]);

  // Pre-check DM eligibility for all coaches with X handles
  useEffect(() => {
    if (staffMode || !school?.coaching_staff || !athleteId) return;
    const coaches = school.coaching_staff.filter(c => c.twitter_handle);
    if (coaches.length === 0) return;

    const checkAll = async () => {
      const results = await Promise.allSettled(
        coaches.map(async (coach) => {
          const handle = coach.twitter_handle!.trim().replace(/\s+/g, '').replace(/@+/g, '');
          const normalizedHandle = handle ? `@${handle}` : '';
          if (!normalizedHandle) return { handle: '', canSend: false };
          // Session cache — the same coaches re-check on every modal open
          // otherwise (one request per coach per open).
          const dmKey = `${athleteId}:${normalizedHandle}`;
          const hit = canSendDMCache.get(dmKey);
          if (hit && Date.now() - hit.t <= CAN_SEND_DM_TTL_MS) {
            return { handle: coach.twitter_handle!, canSend: hit.v };
          }
          try {
            const result = await integrationsAPI.canSendDM(athleteId, normalizedHandle);
            const canSend = result.data?.can_send_dm === true;
            canSendDMCache.set(dmKey, { v: canSend, t: Date.now() });
            return { handle: coach.twitter_handle!, canSend };
          } catch {
            return { handle: coach.twitter_handle!, canSend: false };
          }
        })
      );

      const map: Record<string, boolean> = {};
      results.forEach(r => {
        if (r.status === 'fulfilled' && r.value.handle) {
          map[r.value.handle] = r.value.canSend;
        }
      });
      setCanSendDMMap(map);
    };

    checkAll();
  }, [school, athleteId, staffMode]);

  const loadSchoolData = async () => {
    if (!schoolId || !sessionToken) return;

    // Stale-while-revalidate: paint the last response for this school instantly
    // (no spinner) and still fetch fresh below — the data is athlete-scoped, so
    // the key includes the athlete.
    const cacheKey = `school:${schoolId}:${staffMode ? 'staff' : athleteId || 'anon'}`;
    const cached = swrGet<ComprehensiveSchool>(cacheKey, COMPREHENSIVE_TTL_MS);
    if (cached) {
      setSchool(cached);
    } else {
      setLoading(true);
    }
    try {
      const response = await schoolsAPI.getComprehensiveSchool(
        sessionToken,
        schoolId,
        staffMode ? undefined : (athleteId || undefined)
      );

      if (response.success && response.school) {
        setSchool(response.school);
        setError(null); // clear any prior error so a successful retry recovers (audit #233)
        swrSet(cacheKey, response.school);
      } else {
        throw new Error('Failed to load school data');
      }
    } catch (error) {
      console.error('Error loading school data:', error);
      // With cached data on screen a background-refresh failure is non-fatal.
      if (!cached) setError('Failed to load school information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value?: number) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value?: number) => {
    if (value === undefined || value === null) return 'N/A';
    return `${Math.round(value * 100)}%`;
  };

  const formatNumber = (value?: number) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatBattingAvg = (value?: number | string): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (typeof numValue !== 'number' || isNaN(numValue)) return '.000';
    return numValue.toFixed(3);
  };

  const formatERA = (value?: number | string): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (typeof numValue !== 'number' || isNaN(numValue)) return '0.00';
    return numValue.toFixed(2);
  };

  const formatInningsPitched = (value?: number | string): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (typeof numValue !== 'number' || isNaN(numValue)) return '0.0';
    return numValue.toFixed(1);
  };

  const getStateName = (abbreviation: string): string => {
    const states: { [key: string]: string } = {
      'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
      'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
      'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
      'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
      'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
      'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
      'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
      'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
      'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
      'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
      'DC': 'District of Columbia'
    };
    return states[abbreviation] || abbreviation;
  };

  if (!isOpen) return null;

  // The three coach composers are now one CoachContactModal with a channel
  // selector. Whichever per-coach button was clicked sets its own visibility
  // flag + selected-coach (unchanged, so the §1.5 unlock flow is untouched);
  // here we derive the single channel + coach the shell opens with, and enrich
  // it with the coach's OTHER already-revealed contact info (email/handle) so
  // the athlete can switch channels without reopening. Enrichment only ever
  // uses revealed values (revealedOf / twitterOf), never a fresh unlock.
  const contactChannel: ContactChannel | null =
    isEmailModalVisible ? 'email' : isTweetModalVisible ? 'tweet' : isDMModalVisible ? 'dm' : null;

  let contactCoach: ContactCoach | null = null;
  if (contactChannel === 'email' && selectedEmailCoach) {
    contactCoach = {
      name: selectedEmailCoach.name || '',
      email: selectedEmailCoach.email || '',
      title: selectedEmailCoach.title,
      school: selectedEmailCoach.school,
      coach_id: selectedEmailCoach.coach_id,
      school_id: selectedEmailCoach.school_id,
    };
  } else if (contactChannel === 'tweet' && selectedCoach) {
    contactCoach = {
      name: selectedCoach.name || '',
      twitter_handle: selectedCoach.handle || '',
      coach_id: selectedCoach.coach_id,
      school_id: selectedCoach.school_id,
    };
  } else if (contactChannel === 'dm' && selectedDMCoach) {
    contactCoach = {
      name: selectedDMCoach.name || '',
      twitter_handle: selectedDMCoach.twitter_handle || '',
      coach_id: selectedDMCoach.coach_id,
      school_id: selectedDMCoach.school_id,
    };
  }

  if (contactCoach && contactCoach.coach_id && school?.coaching_staff) {
    const rec = school.coaching_staff.find(
      (c: any) => String(c.production_id) === contactCoach!.coach_id,
    );
    if (rec) {
      if (!contactCoach.email) {
        const em = rec.email || (revealedOf(rec) && revealedOf(rec).email) || '';
        if (em) contactCoach.email = em;
      }
      if (!contactCoach.twitter_handle) {
        const tw = twitterOf(rec);
        if (tw) contactCoach.twitter_handle = tw;
      }
    }
  }

  const closeContact = () => {
    setIsEmailModalVisible(false);
    setSelectedEmailCoach(null);
    setIsTweetModalVisible(false);
    setSelectedCoach(null);
    setIsDMModalVisible(false);
    setSelectedDMCoach(null);
  };

  return (
    <>
      {/* Shared Modal wrapper (audit #223): dialog semantics, Escape, focus trap, scroll lock */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        labelledById="school-profile-title"
        zIndex={1000}
        // While a nested coach modal (tweet/email/DM) is open, ignore backdrop/Escape
        // here so a single Escape closes only the child, not both dialogs (audit #223).
        disableClose={isTweetModalVisible || isEmailModalVisible || isDMModalVisible || !!reportTarget}
        overlayStyle={{ padding: isMobile ? '0' : '20px', fontFamily: 'Manrope, sans-serif' }}
        contentStyle={{
          borderRadius: isMobile ? '0' : '16px',
          maxWidth: isMobile ? '100%' : '900px',
          width: '100%',
          maxHeight: isMobile ? '100%' : '90vh',
          height: isMobile ? '100%' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: isMobile ? '16px' : '24px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px', flex: 1, minWidth: 0 }}>
            {school?.logo_url && (
              <img
                src={school.logo_url}
                alt={`${school.name} logo`}
                style={{ width: isMobile ? '48px' : '60px', height: isMobile ? '48px' : '60px', objectFit: 'contain', flexShrink: 0 }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <div style={{ minWidth: 0 }}>
              <h2 id="school-profile-title" style={{
                fontSize: isMobile ? '18px' : '24px',
                fontWeight: '700',
                color: '#151133',
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {loading ? 'Loading...' : school?.name || 'School Profile'}
              </h2>
              {school && (
                <p style={{ 
                  fontSize: isMobile ? '12px' : '14px', 
                  color: '#6B7280', 
                  margin: '4px 0 0 0' 
                }}>
                  {school.name} • {school.division}
                </p>
              )}
              {/* Header actions — one orange primary (Contact coach, hoisted so it
                  isn't buried behind the non-default coaching tab) + a quiet
                  neutral secondary (Share) so the two don't compete. Aligned in a
                  single flex row rather than stacked inline. */}
              {school && !staffMode && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                  {(school.coaching_staff?.length ?? 0) > 0 && (
                    <button
                      onClick={() => {
                        setActiveTab('coaching');
                        if (contentScrollRef.current) contentScrollRef.current.scrollTop = 0;
                      }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 7,
                        background: '#F9550D', color: 'white', border: 'none', borderRadius: 10,
                        padding: '9px 16px', cursor: 'pointer', fontSize: 13.5, fontWeight: 700,
                        fontFamily: 'Manrope, sans-serif', lineHeight: 1,
                        boxShadow: '0 1px 2px rgba(249,85,13,0.25)', transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#e04a08')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = '#F9550D')}
                    >
                      <Mail size={15} /> Contact coach
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      const url = `${window.location.origin}/school/${schoolSlug(school.name, schoolId || school.school_id || '')}`;
                      try {
                        await navigator.clipboard.writeText(url);
                        setShareCopied(true);
                        setTimeout(() => setShareCopied(false), 2000);
                      } catch { /* clipboard unavailable — no-op */ }
                    }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: 'transparent', border: 'none', padding: '9px 4px', cursor: 'pointer',
                      color: shareCopied ? '#059669' : '#6B7280', fontSize: 13, fontWeight: 600,
                      fontFamily: 'Manrope, sans-serif', lineHeight: 1, transition: 'color 0.15s',
                    }}
                    onMouseEnter={(e) => { if (!shareCopied) e.currentTarget.style.color = '#F9550D'; }}
                    onMouseLeave={(e) => { if (!shareCopied) e.currentTarget.style.color = '#6B7280'; }}
                  >
                    <Share2 size={14} aria-hidden />
                    {shareCopied ? 'Public link copied!' : 'Share public page'}
                  </button>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: 'background 0.2s',
              flexShrink: 0
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#F3F4F6'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <X size={24} color="#6B7280" />
          </button>
        </div>

        {/* Tabs */}
        <div 
          className="hide-scrollbar"
          style={{
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            gap: '8px',
            padding: isMobile ? '0 16px' : '0 24px',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            flexShrink: 0,
            minHeight: '48px'
          }}>
          {(['overview', 'academics', 'coaching', 'athletics', 'roster'] as TabType[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: isMobile ? '12px 16px' : '12px 20px',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab ? '3px solid #F9550D' : '3px solid transparent',
                color: activeTab === tab ? '#F9550D' : '#6B7280',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                textTransform: 'capitalize',
                transition: 'all 0.2s',
                fontFamily: 'Manrope, sans-serif',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div ref={contentScrollRef} style={{
          flex: 1,
          overflow: 'auto',
          overflowAnchor: 'none',
          padding: '24px'
        }}>
          {loading && (
            <div style={{ 
              textAlign: 'center', 
              padding: '48px', 
              color: '#6B7280' 
            }}>
              <div style={{
                display: 'inline-block',
                width: '32px',
                height: '32px',
                border: '4px solid #E5E7EB',
                borderTop: '4px solid #F9550D',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <p style={{ marginTop: '16px' }}>Loading school information...</p>
            </div>
          )}

          {error && !loading && (
            // audit #233: give the error a retry action instead of a dead-end message
            // (the tabs all require !error, so without this the whole modal is blank).
            <ErrorState
              message={error}
              onRetry={() => { setError(null); loadSchoolData(); }}
            />
          )}

          {!loading && !error && school && activeTab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* School Header */}
              <div style={{
                display: 'flex',
                gap: '20px',
                background: 'white',
                padding: '24px',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '1px solid #E5E7EB'
              }}>
                <div style={{ flexShrink: 0 }}>
                  {school.logo_url ? (
                    <img
                      src={school.logo_url}
                      alt={`${school.name} logo`}
                      style={{
                        width: '96px',
                        height: '96px',
                        objectFit: 'contain',
                        borderRadius: '12px',
                        border: '2px solid #F3F4F6',
                        padding: '8px',
                        background: '#FAFAFA'
                      }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <div style={{
                      width: '96px',
                      height: '96px',
                      background: 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #E5E7EB'
                    }}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                  )}
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: '0 0 8px 0' }}>
                    {school.name}
                  </h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {school.academic_stats?.city && school.academic_stats?.state ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                          <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                        <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>
                          {school.academic_stats.city}, {school.academic_stats.state}
                        </span>
                      </div>
                    ) : school.division && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="2" y1="12" x2="22" y2="12"></line>
                          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                        </svg>
                        <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>
                          {school.division}
                        </span>
                      </div>
                    )}
                    {school.conference && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                        <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>
                          {school.conference}
                        </span>
                      </div>
                    )}
                    {school.college_url && (
                      <a
                        href={school.college_url.startsWith('http') ? school.college_url : `https://${school.college_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '14px',
                          color: '#F9550D',
                          textDecoration: 'none',
                          fontWeight: '600',
                          marginTop: '4px',
                          transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                          <polyline points="15 3 21 3 21 9"></polyline>
                          <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                        <span>Visit Website</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Match Score Section - Only show when athlete context is available */}
              {school.matching_data && (
                <div style={{
                  background: 'white',
                  padding: '24px',
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F9550D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                    </svg>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                      Your Match Profile
                    </h3>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* 2x3 Grid - All Match Blocks */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                      gap: '12px'
                    }}>
                      <div style={{
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #ECFDF3 0%, #D1FADF 100%)',
                        padding: '20px 16px',
                        borderRadius: '12px',
                        border: '1px solid #ABEFC6',
                        transition: 'transform 0.2s, box-shadow 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(18, 183, 106, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}>
                        <p style={{ fontSize: '28px', fontWeight: '700', color: '#12B76A', margin: '0 0 8px 0', lineHeight: 1 }}>
                          {Math.round(school.matching_data.match_score_pct)}%
                        </p>
                        <p style={{ fontSize: '12px', color: '#067647', fontWeight: '600', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Overall Match
                        </p>
                      </div>
                      <div style={{
                        textAlign: 'center',
                        background: '#F9FAFB',
                        padding: '20px 16px',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        transition: 'transform 0.2s, box-shadow 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}>
                        <p style={{ margin: '0 0 8px 0', lineHeight: 1 }}>
                          {school.matching_data.state_score > 50
                            ? <Check size={28} color="#10B981" aria-label="In-state" />
                            : <Minus size={28} color="#6B7280" aria-label="Not in-state" />}
                        </p>
                        <p style={{ fontSize: '12px', color: '#6B7280', fontWeight: '600', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          In-State
                        </p>
                      </div>
                      <div style={{
                        textAlign: 'center',
                        background: '#F9FAFB',
                        padding: '20px 16px',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        transition: 'transform 0.2s, box-shadow 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}>
                        <p style={{ fontSize: '28px', fontWeight: '700', color: '#111827', margin: '0 0 8px 0', lineHeight: 1 }}>
                          {Math.round(school.matching_data.pos_score)}%
                        </p>
                        <p style={{ fontSize: '12px', color: '#6B7280', fontWeight: '600', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Position Fit
                        </p>
                      </div>
                      <div style={{
                        textAlign: 'center',
                        background: '#F9FAFB',
                        padding: '20px 16px',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        transition: 'transform 0.2s, box-shadow 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}>
                        <p style={{ fontSize: '28px', fontWeight: '700', color: '#111827', margin: '0 0 8px 0', lineHeight: 1 }}>
                          {Math.round(school.matching_data.academic_score)}%
                        </p>
                        <p style={{ fontSize: '12px', color: '#6B7280', fontWeight: '600', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Academic Fit
                        </p>
                      </div>
                      <div style={{
                        textAlign: 'center',
                        background: '#F9FAFB',
                        padding: '20px 16px',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        transition: 'transform 0.2s, box-shadow 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}>
                        <p style={{ fontSize: '28px', fontWeight: '700', color: '#111827', margin: '0 0 8px 0', lineHeight: 1 }}>
                          {Math.round(school.matching_data.geo_score)}%
                        </p>
                        <p style={{ fontSize: '12px', color: '#6B7280', fontWeight: '600', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Geographic Fit
                        </p>
                      </div>
                      <div style={{
                        textAlign: 'center',
                        background: '#F9FAFB',
                        padding: '20px 16px',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        transition: 'transform 0.2s, box-shadow 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}>
                        <p style={{ fontSize: '28px', fontWeight: '700', color: '#111827', margin: '0 0 8px 0', lineHeight: 1 }}>
                          {Math.round(school.matching_data.athlete_score)}%
                        </p>
                        <p style={{ fontSize: '12px', color: '#6B7280', fontWeight: '600', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Athletic Fit
                        </p>
                      </div>
                    </div>

                    {/* Roster Status - Single Line */}
                    <div style={{
                      padding: '16px 20px',
                      borderRadius: '12px',
                      background: school.matching_data.roster_status === 'Open Roster' ? 'linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)' : 
                                 school.matching_data.roster_status === 'Limited Spots' ? 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' : 
                                 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
                      border: `1px solid ${school.matching_data.roster_status === 'Open Roster' ? '#86EFAC' : 
                                          school.matching_data.roster_status === 'Limited Spots' ? '#FDE68A' : '#FCA5A5'}`
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={school.matching_data.roster_status === 'Open Roster' ? '#15803D' : 
                                 school.matching_data.roster_status === 'Limited Spots' ? '#A16207' : '#DC2626'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                            <circle cx="9" cy="7" r="4"></circle>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                          </svg>
                          <span style={{
                            fontSize: '16px',
                            fontWeight: '700',
                            color: school.matching_data.roster_status === 'Open Roster' ? '#15803D' : 
                                   school.matching_data.roster_status === 'Limited Spots' ? '#A16207' : '#DC2626'
                          }}>
                            {school.matching_data.roster_status}
                          </span>
                        </div>
                        <span style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: school.matching_data.roster_status === 'Open Roster' ? '#15803D' : 
                                 school.matching_data.roster_status === 'Limited Spots' ? '#A16207' : '#DC2626'
                        }}>
                          {/* #123: the estimated_graduation_year here is clamped to the
                              latest roster-openings class, so showing it as "for 2029" to a
                              2031 athlete misrepresents their own class. Drop the specific
                              year and describe the class it actually refers to. */}
                          {school.matching_data.openings} openings for the latest recruiting class
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Team Performance Stats */}
              {school.school_stats && (
                <div style={{
                  background: 'white',
                  padding: '24px',
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F9550D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="7"></circle>
                      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
                    </svg>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                      Team Performance ({school.school_stats.season})
                    </h3>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '12px' }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)',
                      padding: '16px',
                      borderRadius: '12px',
                      border: '1px solid #FED7AA',
                      textAlign: 'center',
                      transition: 'transform 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                      <p style={{ fontSize: '24px', fontWeight: '700', color: '#F9550D', margin: '0 0 6px 0', lineHeight: 1 }}>
                        {school.school_stats.wins}-{school.school_stats.losses}
                      </p>
                      <p style={{ fontSize: '11px', color: '#9A3412', fontWeight: '600', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Record
                      </p>
                    </div>
                    <div style={{
                      background: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)',
                      padding: '16px',
                      borderRadius: '12px',
                      border: '1px solid #FED7AA',
                      textAlign: 'center',
                      transition: 'transform 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                      <p style={{ fontSize: '24px', fontWeight: '700', color: '#F9550D', margin: '0 0 6px 0', lineHeight: 1 }}>
                        {Math.round(school.school_stats.win_pct * 100)}%
                      </p>
                      <p style={{ fontSize: '11px', color: '#9A3412', fontWeight: '600', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Win %
                      </p>
                    </div>
                    <div style={{
                      background: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)',
                      padding: '16px',
                      borderRadius: '12px',
                      border: '1px solid #FED7AA',
                      textAlign: 'center',
                      transition: 'transform 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                      <p style={{ fontSize: '24px', fontWeight: '700', color: '#F9550D', margin: '0 0 6px 0', lineHeight: 1 }}>
                        {school.school_stats.player_count}
                      </p>
                      <p style={{ fontSize: '11px', color: '#9A3412', fontWeight: '600', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Roster Size
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Facts */}
              {school.academic_stats && (
                <div style={{
                  background: 'white',
                  padding: '24px',
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F9550D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
                    </svg>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                      Quick Facts
                    </h3>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '16px', marginBottom: '20px' }}>
                    <div style={{ textAlign: 'center', padding: '12px' }}>
                      <div style={{ 
                        width: '48px', 
                        height: '48px', 
                        borderRadius: '12px', 
                        background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 12px auto',
                        border: '1px solid #FDE68A'
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                      </div>
                      <p style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: '0 0 4px 0', lineHeight: 1 }}>
                        {formatNumber(school.academic_stats.undergraduate_student_count)}
                      </p>
                      <p style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500', margin: 0 }}>
                        Students
                      </p>
                    </div>
                    <div style={{ textAlign: 'center', padding: '12px' }}>
                      <div style={{ 
                        width: '48px', 
                        height: '48px', 
                        borderRadius: '12px', 
                        background: 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 12px auto',
                        border: '1px solid #BFDBFE'
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                      </div>
                      <p style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: '0 0 4px 0', lineHeight: 1 }}>
                        {formatPercentage(school.academic_stats.admission_rate)}
                      </p>
                      <p style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500', margin: 0 }}>
                        Accept Rate
                      </p>
                    </div>
                    <div style={{ textAlign: 'center', padding: '12px' }}>
                      <div style={{ 
                        width: '48px', 
                        height: '48px', 
                        borderRadius: '12px', 
                        background: 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 12px auto',
                        border: '1px solid #C7D2FE'
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                        </svg>
                      </div>
                      <p style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: '0 0 4px 0', lineHeight: 1 }}>
                        {school.academic_stats.avg_sat_score ? Math.round(school.academic_stats.avg_sat_score) : 'N/A'}
                      </p>
                      <p style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500', margin: 0 }}>
                        Avg SAT
                      </p>
                    </div>
                  </div>
                  {school.academic_stats.school_type && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      paddingTop: '20px',
                      borderTop: '1px solid #E5E7EB'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                          <polyline points="9 22 9 12 15 12 15 22"></polyline>
                        </svg>
                        <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>School Type:</span>
                        <span style={{ fontSize: '14px', color: '#111827', fontWeight: '700' }}>
                          {school.academic_stats.school_type}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Cost Overview */}
              {school.academic_stats && (
                <div style={{
                  background: 'white',
                  padding: '24px',
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F9550D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="1" x2="12" y2="23"></line>
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                    </svg>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                      Cost Overview
                    </h3>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '14px 0',
                      borderBottom: '1px solid #F3F4F6'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ 
                          width: '8px', 
                          height: '8px', 
                          borderRadius: '50%', 
                          background: '#10B981' 
                        }} />
                        <span style={{ fontSize: '14px', color: '#4B5563', fontWeight: '500' }}>
                          In-State Tuition
                        </span>
                      </div>
                      <span style={{ fontSize: '15px', color: '#111827', fontWeight: '700' }}>
                        {formatCurrency(school.academic_stats.avg_tuition_in_state)}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '14px 0',
                      borderBottom: '1px solid #F3F4F6'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ 
                          width: '8px', 
                          height: '8px', 
                          borderRadius: '50%', 
                          background: '#F59E0B' 
                        }} />
                        <span style={{ fontSize: '14px', color: '#4B5563', fontWeight: '500' }}>
                          Out-of-State Tuition
                        </span>
                      </div>
                      <span style={{ fontSize: '15px', color: '#111827', fontWeight: '700' }}>
                        {formatCurrency(school.academic_stats.avg_tuition_out_of_state)}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '14px 0',
                      borderBottom: '1px solid #F3F4F6'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ 
                          width: '8px', 
                          height: '8px', 
                          borderRadius: '50%', 
                          background: '#8B5CF6' 
                        }} />
                        <span style={{ fontSize: '14px', color: '#4B5563', fontWeight: '500' }}>
                          Room & Board
                        </span>
                      </div>
                      <span style={{ fontSize: '15px', color: '#111827', fontWeight: '700' }}>
                        {formatCurrency(school.academic_stats.avg_room_board_on_campus_cost)}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '14px 0',
                      background: '#F3F4F6',
                      borderRadius: '8px',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      marginTop: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                          <polyline points="17 6 23 6 23 12"></polyline>
                        </svg>
                        <span style={{ fontSize: '14px', color: '#111827', fontWeight: '700' }}>
                          Total Cost
                        </span>
                      </div>
                      <span style={{ fontSize: '16px', color: '#111827', fontWeight: '700' }}>
                        {formatCurrency(school.academic_stats.avg_cost_of_attendance)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Athletic Program */}
              <div style={{
                background: 'white',
                padding: '24px',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '1px solid #E5E7EB'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F9550D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="7"></circle>
                    <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
                  </svg>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                    Athletic Program
                  </h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '14px',
                    padding: '12px 16px',
                    background: '#F9FAFB',
                    borderRadius: '10px',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="8" r="7"></circle>
                        <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
                      </svg>
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Division
                      </p>
                      <p style={{ fontSize: '15px', color: '#111827', fontWeight: '700', margin: 0 }}>
                        {school.division || 'Other'}
                      </p>
                    </div>
                  </div>
                  {school.conference && (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '14px',
                      padding: '12px 16px',
                      background: '#F9FAFB',
                      borderRadius: '10px',
                      border: '1px solid #E5E7EB'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Conference
                        </p>
                        <p style={{ fontSize: '15px', color: '#111827', fontWeight: '700', margin: 0 }}>
                          {school.conference}
                        </p>
                      </div>
                    </div>
                  )}
                  {school.head_coach && (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '14px',
                      padding: '12px 16px',
                      background: '#F9FAFB',
                      borderRadius: '10px',
                      border: '1px solid #E5E7EB'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Head Coach
                        </p>
                        <p style={{ fontSize: '15px', color: '#111827', fontWeight: '700', margin: 0 }}>
                          {school.head_coach}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {!loading && !error && school && activeTab === 'academics' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {school.academic_stats && (
                <>
                  {/* Academic Profile */}
                  <div style={{
                    background: 'white',
                    padding: '24px',
                    borderRadius: '16px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F9550D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                      </svg>
                      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                        Academic Profile
                      </h3>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '16px' }}>
                      <div style={{ 
                        textAlign: 'center',
                        padding: '20px 16px',
                        background: '#F9FAFB',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB'
                      }}>
                        <p style={{ fontSize: '12px', color: '#6B7280', fontWeight: '600', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Admission Rate
                        </p>
                        <p style={{ 
                          fontSize: '24px', 
                          fontWeight: '700', 
                          color: (school.academic_stats.admission_rate || 0) < 0.3 ? '#EF4444' : '#10B981',
                          margin: 0,
                          lineHeight: 1
                        }}>
                          {formatPercentage(school.academic_stats.admission_rate)}
                        </p>
                      </div>
                      <div style={{ 
                        textAlign: 'center',
                        padding: '20px 16px',
                        background: '#F9FAFB',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB'
                      }}>
                        <p style={{ fontSize: '12px', color: '#6B7280', fontWeight: '600', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Completion Rate
                        </p>
                        <p style={{ fontSize: '24px', fontWeight: '700', color: '#10B981', margin: 0, lineHeight: 1 }}>
                          {formatPercentage(school.academic_stats.completion_rate_undergraduate)}
                        </p>
                      </div>
                      <div style={{ 
                        textAlign: 'center',
                        padding: '20px 16px',
                        background: '#F9FAFB',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB'
                      }}>
                        <p style={{ fontSize: '12px', color: '#6B7280', fontWeight: '600', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Transfer Rate
                        </p>
                        <p style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: 0, lineHeight: 1 }}>
                          {formatPercentage(school.academic_stats.transfer_rate_undergraduate)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Test Scores */}
                  <div style={{
                    background: 'white',
                    padding: '24px',
                    borderRadius: '16px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F9550D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                        Average Test Scores
                      </h3>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {/* SAT Section */}
                      <div style={{
                        padding: '20px',
                        background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
                        borderRadius: '12px',
                        border: '2px solid #3B82F6'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '48px',
                            height: '48px',
                            borderRadius: '10px',
                            background: 'white',
                            border: '2px solid #3B82F6',
                            flexShrink: 0
                          }}>
                            <span style={{ fontSize: '18px', fontWeight: '700', color: '#3B82F6' }}>SAT</span>
                          </div>
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: '13px', color: '#1E40AF', fontWeight: '600', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Scholastic Assessment Test
                            </p>
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '12px' }}>
                          <div style={{ 
                            textAlign: 'center',
                            padding: '16px 12px',
                            background: 'white',
                            borderRadius: '10px',
                            border: '1px solid #BFDBFE'
                          }}>
                            <p style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Total Score
                            </p>
                            <p style={{ fontSize: '28px', fontWeight: '700', color: '#3B82F6', margin: 0, lineHeight: 1 }}>
                              {school.academic_stats.avg_sat_score ? Math.round(school.academic_stats.avg_sat_score) : 'N/A'}
                            </p>
                            <p style={{ fontSize: '10px', color: '#9CA3AF', margin: '4px 0 0 0' }}>
                              out of 1600
                            </p>
                          </div>
                          <div style={{ 
                            textAlign: 'center',
                            padding: '16px 12px',
                            background: 'white',
                            borderRadius: '10px',
                            border: '1px solid #BFDBFE'
                          }}>
                            <p style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Math
                            </p>
                            <p style={{ fontSize: '28px', fontWeight: '700', color: '#3B82F6', margin: 0, lineHeight: 1 }}>
                              {school.academic_stats.avg_math_sat_score ? Math.round(school.academic_stats.avg_math_sat_score) : 'N/A'}
                            </p>
                            <p style={{ fontSize: '10px', color: '#9CA3AF', margin: '4px 0 0 0' }}>
                              out of 800
                            </p>
                          </div>
                          <div style={{ 
                            textAlign: 'center',
                            padding: '16px 12px',
                            background: 'white',
                            borderRadius: '10px',
                            border: '1px solid #BFDBFE'
                          }}>
                            <p style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Reading
                            </p>
                            <p style={{ fontSize: '28px', fontWeight: '700', color: '#3B82F6', margin: 0, lineHeight: 1 }}>
                              {school.academic_stats.avg_reading_sat_score ? Math.round(school.academic_stats.avg_reading_sat_score) : 'N/A'}
                            </p>
                            <p style={{ fontSize: '10px', color: '#9CA3AF', margin: '4px 0 0 0' }}>
                              out of 800
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* ACT Section */}
                      <div style={{
                        padding: '20px',
                        background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
                        borderRadius: '12px',
                        border: '2px solid #6366F1'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '48px',
                            height: '48px',
                            borderRadius: '10px',
                            background: 'white',
                            border: '2px solid #6366F1',
                            flexShrink: 0
                          }}>
                            <span style={{ fontSize: '18px', fontWeight: '700', color: '#6366F1' }}>ACT</span>
                          </div>
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: '13px', color: '#4338CA', fontWeight: '600', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              American College Testing
                            </p>
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '12px' }}>
                          <div style={{ 
                            textAlign: 'center',
                            padding: '16px 12px',
                            background: 'white',
                            borderRadius: '10px',
                            border: '1px solid #C7D2FE'
                          }}>
                            <p style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Composite
                            </p>
                            <p style={{ fontSize: '28px', fontWeight: '700', color: '#6366F1', margin: 0, lineHeight: 1 }}>
                              {school.academic_stats.avg_act_score ? Math.round(school.academic_stats.avg_act_score) : 'N/A'}
                            </p>
                            <p style={{ fontSize: '10px', color: '#9CA3AF', margin: '4px 0 0 0' }}>
                              out of 36
                            </p>
                          </div>
                          <div style={{ 
                            textAlign: 'center',
                            padding: '16px 12px',
                            background: 'white',
                            borderRadius: '10px',
                            border: '1px solid #C7D2FE'
                          }}>
                            <p style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Math
                            </p>
                            <p style={{ fontSize: '28px', fontWeight: '700', color: '#6366F1', margin: 0, lineHeight: 1 }}>
                              {school.academic_stats.avg_math_act_score ? Math.round(school.academic_stats.avg_math_act_score) : 'N/A'}
                            </p>
                            <p style={{ fontSize: '10px', color: '#9CA3AF', margin: '4px 0 0 0' }}>
                              out of 36
                            </p>
                          </div>
                          <div style={{ 
                            textAlign: 'center',
                            padding: '16px 12px',
                            background: 'white',
                            borderRadius: '10px',
                            border: '1px solid #C7D2FE'
                          }}>
                            <p style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              English
                            </p>
                            <p style={{ fontSize: '28px', fontWeight: '700', color: '#6366F1', margin: 0, lineHeight: 1 }}>
                              {school.academic_stats.avg_english_act_score ? Math.round(school.academic_stats.avg_english_act_score) : 'N/A'}
                            </p>
                            <p style={{ fontSize: '10px', color: '#9CA3AF', margin: '4px 0 0 0' }}>
                              out of 36
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Student Body */}
                  <div style={{
                    background: 'white',
                    padding: '24px',
                    borderRadius: '16px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F9550D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                        Student Body
                      </h3>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
                      <div style={{ 
                        textAlign: 'center',
                        padding: '16px',
                        background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                        borderRadius: '12px',
                        border: '1px solid #FDE68A'
                      }}>
                        <p style={{ fontSize: '20px', fontWeight: '700', color: '#92400E', margin: '0 0 4px 0', lineHeight: 1 }}>
                          {formatNumber(school.academic_stats.undergraduate_student_count)}
                        </p>
                        <p style={{ fontSize: '11px', color: '#92400E', fontWeight: '600', margin: 0 }}>
                          Undergrads
                        </p>
                      </div>
                      <div style={{ 
                        textAlign: 'center',
                        padding: '16px',
                        background: 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)',
                        borderRadius: '12px',
                        border: '1px solid #C7D2FE'
                      }}>
                        <p style={{ fontSize: '20px', fontWeight: '700', color: '#4338CA', margin: '0 0 4px 0', lineHeight: 1 }}>
                          {formatNumber(school.academic_stats.graduate_student_count)}
                        </p>
                        <p style={{ fontSize: '11px', color: '#4338CA', fontWeight: '600', margin: 0 }}>
                          Graduates
                        </p>
                      </div>
                      <div style={{ 
                        textAlign: 'center',
                        padding: '16px',
                        background: 'linear-gradient(135deg, #FCE7F3 0%, #FBCFE8 100%)',
                        borderRadius: '12px',
                        border: '1px solid #FBCFE8'
                      }}>
                        <p style={{ fontSize: '20px', fontWeight: '700', color: '#BE185D', margin: '0 0 4px 0', lineHeight: 1 }}>
                          {formatPercentage(school.academic_stats.undegraduate_women_percent || 0)}
                        </p>
                        <p style={{ fontSize: '11px', color: '#BE185D', fontWeight: '600', margin: 0 }}>
                          Women
                        </p>
                      </div>
                      <div style={{ 
                        textAlign: 'center',
                        padding: '16px',
                        background: 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)',
                        borderRadius: '12px',
                        border: '1px solid #BFDBFE'
                      }}>
                        <p style={{ fontSize: '20px', fontWeight: '700', color: '#1E40AF', margin: '0 0 4px 0', lineHeight: 1 }}>
                          {formatPercentage(school.academic_stats.undegraduate_men_percent || 0)}
                        </p>
                        <p style={{ fontSize: '11px', color: '#1E40AF', fontWeight: '600', margin: 0 }}>
                          Men
                        </p>
                      </div>
                    </div>
                    <div style={{
                      paddingTop: '20px',
                      borderTop: '1px solid #E5E7EB'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="1" x2="12" y2="23"></line>
                          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                        </svg>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Financial Aid</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                        <div style={{ 
                          textAlign: 'center',
                          padding: '12px',
                          background: '#F0FDF4',
                          borderRadius: '10px',
                          border: '1px solid #BBF7D0'
                        }}>
                          <p style={{ fontSize: '18px', fontWeight: '700', color: '#15803D', margin: '0 0 4px 0', lineHeight: 1 }}>
                            {formatPercentage(school.academic_stats.percent_undergraduates_pell_grant || 0)}
                          </p>
                          <p style={{ fontSize: '11px', color: '#15803D', fontWeight: '600', margin: 0 }}>
                            Receive Pell Grant
                          </p>
                        </div>
                        <div style={{ 
                          textAlign: 'center',
                          padding: '12px',
                          background: '#F0FDF4',
                          borderRadius: '10px',
                          border: '1px solid #BBF7D0'
                        }}>
                          <p style={{ fontSize: '18px', fontWeight: '700', color: '#15803D', margin: '0 0 4px 0', lineHeight: 1 }}>
                            {formatPercentage(school.academic_stats.percent_undergraduates_with_federal_loan || 0)}
                          </p>
                          <p style={{ fontSize: '11px', color: '#15803D', fontWeight: '600', margin: 0 }}>
                            Federal Loans
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Majors Offered */}
              {school.degrees && school.degrees.length > 0 && (
                <div style={{
                  background: 'white',
                  padding: '24px',
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F9550D" strokeWidth="2">
                      <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                      <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
                    </svg>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                      Majors Offered ({school.degrees.length})
                    </h3>
                  </div>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    {school.degrees.map((degree: string) => (
                      <span
                        key={degree}
                        style={{
                          display: 'inline-block',
                          background: '#F9FAFB',
                          border: '1px solid #E5E7EB',
                          color: '#374151',
                          fontSize: '13px',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          lineHeight: '1.3'
                        }}
                      >
                        {degree}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!loading && !error && school && activeTab === 'coaching' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {unlockStatus && !unlockStatus.isPaid && typeof unlockStatus.remaining === 'number' && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 14px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                  fontFamily: 'Manrope, sans-serif',
                  background: unlockStatus.remaining > 0 ? '#ecfdf3' : '#fff7ed',
                  color: unlockStatus.remaining > 0 ? '#067647' : '#b5390a',
                  border: `1px solid ${unlockStatus.remaining > 0 ? '#c6f0da' : '#fcd9c4'}`,
                }}>
                  {unlockStatus.remaining > 0
                    ? `${unlockStatus.remaining} of ${unlockStatus.limit || 3} free programs left — unlock a school to message its whole coaching staff.`
                    : "You've used your 3 free programs. Upgrade to message every school."}
                </div>
              )}
              {(() => {
                const coaches = school.coaching_staff || [];
                
                // Sort coaches to put head coach first
                const sortedCoaches = [...coaches].sort((a, b) => {
                  const aIsHead = a.position?.toLowerCase().includes('head') || a.title?.toLowerCase().includes('head');
                  const bIsHead = b.position?.toLowerCase().includes('head') || b.title?.toLowerCase().includes('head');
                  
                  if (aIsHead && !bIsHead) return -1;
                  if (!aIsHead && bIsHead) return 1;
                  return 0;
                });

                const schoolLocked = sortedCoaches.some(isCoachLocked);
                const remainingPrograms = unlockStatus?.remaining ?? (unlockStatus?.limit ?? 3);
                return sortedCoaches.length > 0 ? (
                  <>
                    {schoolLocked && (
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: '16px', flexWrap: 'wrap', padding: '18px 20px', borderRadius: '14px',
                        background: 'rgba(249,85,13,0.06)', border: '1px solid rgba(249,85,13,0.25)',
                        fontFamily: 'Manrope, sans-serif',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                          <div style={{
                            width: '44px', height: '44px', borderRadius: '12px', flexShrink: 0,
                            background: 'rgba(249,85,13,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <Lock size={22} color="#F9550D" />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>
                              Unlock {school.name} to message the coaching staff
                            </div>
                            <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '2px' }}>
                              {remainingPrograms > 0
                                ? `Uses 1 of your ${remainingPrograms} free program${remainingPrograms === 1 ? '' : 's'} — reach every coach on staff by email, tweet, and DM.`
                                : "You've used all your free programs — upgrade to message every coach on staff."}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => { const c = sortedCoaches.find(isCoachLocked); if (c) handleUnlockClick(c); }}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px', flexShrink: 0,
                            padding: '11px 20px', background: '#F9550D', borderRadius: '10px',
                            border: 'none', fontSize: '14px', fontWeight: '700', color: 'white',
                            cursor: 'pointer', fontFamily: 'Manrope, sans-serif',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#E04A0B'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = '#F9550D'; }}
                        >
                          <Lock size={16} /> {remainingPrograms > 0 ? 'Unlock program' : 'Upgrade'}
                        </button>
                      </div>
                    )}
                    {sortedCoaches.map((coach, index) => (
                    <div key={coach.production_id || index} style={{
                      background: 'white',
                      padding: '24px',
                      borderRadius: '16px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #E5E7EB'
                    }}>
                      {/* Coach Header */}
                      <div style={{ display: 'flex', gap: '16px', marginBottom: coach.bio_summary ? '16px' : '20px' }}>
                        <div style={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #F9550D 0%, #DC2626 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          boxShadow: '0 4px 6px rgba(249, 85, 13, 0.2)'
                        }}>
                          {isCoachLocked(coach) ? (
                            <Lock size={26} color="white" />
                          ) : (
                            <span style={{ fontSize: '22px', fontWeight: '700', color: 'white' }}>
                              {coach.coach_name.split(' ').map(n => n[0]).join('')}
                            </span>
                          )}
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            {coach.coach_name}
                            {isCoachLocked(coach) && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: '700', color: '#F9550D', background: 'rgba(249,85,13,0.1)', padding: '2px 8px', borderRadius: '999px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                <Lock size={11} /> Locked
                              </span>
                            )}
                          </h3>
                          <p style={{ fontSize: '15px', fontWeight: '600', color: '#F9550D', margin: '0 0 4px 0' }}>
                            {coach.position}
                          </p>
                          {coach.title && coach.title !== coach.position && (
                            <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
                              {coach.title}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Bio Summary */}
                      {coach.bio_summary && (
                        <p style={{ 
                          fontSize: '14px', 
                          color: '#4B5563', 
                          lineHeight: '1.6',
                          margin: '0 0 20px 0',
                          padding: '16px',
                          background: '#F9FAFB',
                          borderRadius: '10px',
                          borderLeft: '3px solid #F9550D'
                        }}>
                          {coach.bio_summary}
                        </p>
                      )}

                      {/* Contact Buttons — dim + block taps while this coach's unlock is in flight */}
                      <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: '10px',
                        ...(unlockingCoachId === (coach.production_id != null ? String(coach.production_id) : '')
                          ? { opacity: 0.5, pointerEvents: 'none' as const } : {}),
                      }}>
                        {/* §1.5: a LOCKED coach shows no per-coach buttons — the
                            program is unlocked as a whole via the single CTA above
                            the staff list, which reveals every coach at once. */}
                        {/* P2.5: render the button whenever the coach HAS a
                            phone (paid → real number; free → has_phone flag
                            with the value redacted). Clicking gates via
                            requirePaid for free users. */}
                        {!isCoachLocked(coach) && (coach.phone || coach.has_phone) && (
                          staffMode ? (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '8px',
                              padding: '10px 16px', background: '#F9FAFB', borderRadius: '8px',
                              border: '1px solid #E5E7EB', fontSize: '14px', fontWeight: '500', color: '#374151',
                            }}>
                              <Phone size={16} color="#6B7280" />
                              {coach.phone}
                            </span>
                          ) : phoneOf(coach) ? (
                          <a
                            href={`tel:${phoneOf(coach)}`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '10px 16px',
                              background: '#FEF3F2',
                              borderRadius: '8px',
                              border: '1px solid #FED7AA',
                              textDecoration: 'none',
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#F9550D',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#FFEDD5';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#FEF3F2';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            <Phone size={16} />
                            <span>Phone</span>
                          </a>
                          ) : (
                          <button
                            onClick={() => unlockCoachThen(coach, (c) => { if (c.phone) window.location.href = `tel:${c.phone}`; })}
                            disabled={unlockingCoachId === (coach.production_id != null ? String(coach.production_id) : '')}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '10px 16px',
                              background: '#F9FAFB',
                              borderRadius: '8px',
                              border: '1px solid #E5E7EB',
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#9CA3AF',
                              cursor: 'pointer',
                              fontFamily: 'Manrope, sans-serif',
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                            </svg>
                            <span>Phone</span>
                          </button>
                          )
                        )}
                        {!isCoachLocked(coach) && (coach.email || coach.has_email) && (
                          staffMode ? (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '8px',
                              padding: '10px 16px', background: '#F9FAFB', borderRadius: '8px',
                              border: '1px solid #E5E7EB', fontSize: '14px', fontWeight: '500', color: '#374151',
                            }}>
                              <Mail size={16} color="#6B7280" />
                              {coach.email}
                            </span>
                          ) : (
                          <button
                            onClick={() => handleEmailCoach(coach)}
                            disabled={unlockingCoachId === (coach.production_id != null ? String(coach.production_id) : '')}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '10px 16px',
                              background: '#FEF2F2',
                              borderRadius: '8px',
                              border: '1px solid #FECACA',
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#EA4335',
                              transition: 'all 0.2s',
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#FEE2E2';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#FEF2F2';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            <Mail size={16} />
                            <span>Email</span>
                          </button>
                          )
                        )}
                        {!isCoachLocked(coach) && (coach.twitter_handle || coach.has_twitter_handle) && (
                          staffMode ? (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '8px',
                              padding: '10px 16px', background: '#F9FAFB', borderRadius: '8px',
                              border: '1px solid #E5E7EB', fontSize: '14px', fontWeight: '500', color: '#374151',
                            }}>
                              <XIcon size={16} color="#6B7280" />
                              @{coach.twitter_handle?.replace(/^@/, '')}
                            </span>
                          ) : (
                          <button
                            onClick={() => {
                              const openTweet = (h: string) => {
                                setSelectedCoach({
                                  name: coach.coach_name,
                                  handle: h || '',
                                  coach_id: coach.production_id?.toString(),
                                  school_id: schoolId?.toString()
                                });
                                setIsTweetModalVisible(true);
                              };
                              // §1.5: if the handle is still locked (free), spend an
                              // unlock to reveal it, then open the composer.
                              const handle = twitterOf(coach);
                              if (!handle) { unlockCoachThen(coach, (c) => openTweet(c.twitter_handle)); return; }
                              openTweet(handle);
                            }}
                            disabled={unlockingCoachId === (coach.production_id != null ? String(coach.production_id) : '')}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '10px 16px',
                              background: '#F3F4F6',
                              borderRadius: '8px',
                              border: '1px solid #E5E7EB',
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#000000',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#E5E7EB';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#F3F4F6';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            <XIcon size={16} />
                            <span>Tweet</span>
                          </button>
                          )
                        )}
                        {/* DM Button - show whenever coach has an X
                            handle (paid: real handle + canSendDM lookup;
                            free: has_twitter_handle flag; gated by
                            requirePaid). The canSendDM lookup is only
                            done for paid users since it uses the real
                            handle as the key. */}
                        {!isCoachLocked(coach) && !staffMode && (
                          coach.twitter_handle
                            // Reserve the slot while the canSendDM check is in flight
                            // (undefined); only hide the button when it's explicitly
                            // false, so the contact grid doesn't reflow when it lands.
                            ? canSendDMMap[coach.twitter_handle] !== false
                            : !!coach.has_twitter_handle
                        ) && (
                          <button
                            onClick={() => {
                              const openDM = (h: string) => {
                                setSelectedDMCoach({
                                  name: coach.coach_name,
                                  twitter_handle: h || '',
                                  coach_id: coach.production_id?.toString(),
                                  school_id: schoolId?.toString()
                                });
                                setIsDMModalVisible(true);
                              };
                              // §1.5: unlock to reveal the handle (free), then open the DM.
                              const handle = twitterOf(coach);
                              if (!handle) { unlockCoachThen(coach, (c) => openDM(c.twitter_handle)); return; }
                              openDM(handle);
                            }}
                            disabled={unlockingCoachId === (coach.production_id != null ? String(coach.production_id) : '') || (!!coach.twitter_handle && canSendDMMap[coach.twitter_handle] === undefined)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '10px 16px',
                              opacity: (!!coach.twitter_handle && canSendDMMap[coach.twitter_handle] === undefined) ? 0.5 : 1,
                              background: '#F0F9FF',
                              borderRadius: '8px',
                              border: '1px solid #BAE6FD',
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#0284C7',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#E0F2FE';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#F0F9FF';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            <XIcon size={16} />
                            <span>DM</span>
                          </button>
                        )}
                        {/* APS-260: families flag stale coach data for staff review. */}
                        {!staffMode && coach.production_id != null && (
                          <button
                            type="button"
                            onClick={() => setReportTarget({ coach: toReportCoach(coach) })}
                            aria-label={`Report a change for ${coach.coach_name || 'this coach'}`}
                            style={{ ...reportLinkStyle, marginLeft: 'auto' }}
                          >
                            <Flag size={14} aria-hidden="true" />
                            Report a change
                          </button>
                        )}
                      </div>

                      {/* Coach Tweets Section - only show if coach has an X handle (not in staff mode) */}
                      {!staffMode && coach.production_id && coach.twitter_handle && (
                        <CoachTweetsSection
                          coachId={coach.production_id}
                          coachName={coach.coach_name}
                        />
                      )}
                    </div>
                  ))}
                    {!staffMode && (
                      <div>
                        <button
                          type="button"
                          onClick={() => setReportTarget({
                            coach: null,
                            coaches: sortedCoaches.filter((c) => c.production_id != null).map(toReportCoach),
                            kind: 'missing',
                          })}
                          style={reportLinkStyle}
                        >
                          <UserPlus size={14} aria-hidden="true" />
                          Report a missing coach
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{
                    background: 'white',
                    padding: '64px 24px',
                    borderRadius: '16px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #E5E7EB',
                    textAlign: 'center'
                  }}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 16px' }}>
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    <p style={{ fontSize: '16px', fontWeight: '600', color: '#6B7280', margin: '0 0 8px 0' }}>
                      No coaching staff information available
                    </p>
                    <p style={{ fontSize: '14px', color: '#9CA3AF', margin: 0 }}>
                      Coach details will be displayed when available
                    </p>
                    {!staffMode && (
                      <button
                        type="button"
                        onClick={() => setReportTarget({ coach: null, coaches: [], kind: 'missing' })}
                        style={{ ...reportLinkStyle, marginTop: '12px' }}
                      >
                        <UserPlus size={14} aria-hidden="true" />
                        Report a missing coach
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {!loading && !error && school && activeTab === 'athletics' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Athletic Information */}
              <div style={{
                background: 'white',
                padding: '24px',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '1px solid #E5E7EB'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F9550D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="7"></circle>
                    <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
                  </svg>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                    Athletic Information
                  </h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: school.school_sports_url ? '20px' : 0 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px',
                    background: '#F9FAFB',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="8" r="7"></circle>
                        <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
                      </svg>
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Division
                      </p>
                      <p style={{ fontSize: '16px', color: '#111827', fontWeight: '700', margin: 0 }}>
                        {school.division || 'N/A'}
                      </p>
                    </div>
                  </div>
                  {school.conference && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px',
                      background: '#F9FAFB',
                      borderRadius: '12px',
                      border: '1px solid #E5E7EB'
                    }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Conference
                        </p>
                        <p style={{ fontSize: '16px', color: '#111827', fontWeight: '700', margin: 0 }}>
                          {school.conference}
                        </p>
                      </div>
                    </div>
                  )}
                  {school.head_coach && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px',
                      background: '#F9FAFB',
                      borderRadius: '12px',
                      border: '1px solid #E5E7EB'
                    }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Head Coach
                        </p>
                        <p style={{ fontSize: '16px', color: '#111827', fontWeight: '700', margin: 0 }}>
                          {school.head_coach}
                        </p>
                      </div>
                    </div>
                  )}
                  {school.max_season_year && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px',
                      background: '#F9FAFB',
                      borderRadius: '12px',
                      border: '1px solid #E5E7EB'
                    }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Current Season
                        </p>
                        <p style={{ fontSize: '16px', color: '#111827', fontWeight: '700', margin: 0 }}>
                          {school.max_season_year}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                {school.school_sports_url && (
                  <a
                    href={school.school_sports_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      padding: '14px 24px',
                      background: 'linear-gradient(135deg, #F9550D 0%, #DC2626 100%)',
                      borderRadius: '10px',
                      textDecoration: 'none',
                      fontSize: '15px',
                      fontWeight: '700',
                      color: 'white',
                      boxShadow: '0 4px 6px rgba(249, 85, 13, 0.2)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 12px rgba(249, 85, 13, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 6px rgba(249, 85, 13, 0.2)';
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                    <span>View Athletic Website</span>
                  </a>
                )}
              </div>

              {/* Roster need and position openings. The band shows whenever there is a need, same as the card. */}
              {(() => {
                const need = rosterNeed({
                  athlete_position: school.matching_data?.athlete_position,
                  roster_status: school.matching_data?.roster_status,
                  openings: school.matching_data?.openings,
                  position_openings: school.position_openings,
                  athlete_graduation_year: school.athlete_graduation_year,
                });
                const positionOpenings = school.position_openings || [];
                const hasOpenings = positionOpenings.length > 0;
                if (!need && !hasOpenings) return null;
                return (
                <div style={{
                  background: 'white',
                  padding: '24px',
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #E5E7EB'
                }}>
                  <RosterNeedBand
                    need={need}
                    style={hasOpenings ? { marginBottom: '20px' } : undefined}
                  />
                  {hasOpenings && (
                  <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F9550D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                    </svg>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                      Position Openings for Your Class
                    </h3>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {positionOpenings.map((opening, index) => (
                      <div key={`${opening.position_name}-${opening.estimated_graduation_year}-${index}`} style={{
                        padding: '16px',
                        background: '#F9FAFB',
                        borderRadius: '10px',
                        border: '1px solid #E5E7EB'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>
                            {opening.position_name}
                          </span>
                          <div style={{
                            padding: '4px 10px',
                            background: 'linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)',
                            borderRadius: '12px',
                            border: '1px solid #86EFAC'
                          }}>
                            <span style={{ fontSize: '14px', fontWeight: '700', color: '#15803D' }}>
                              {opening.count}
                            </span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6B7280' }}>
                          <span>
                            {school.athlete_graduation_year != null
                              && Number(opening.estimated_graduation_year) !== Number(school.athlete_graduation_year)
                              ? 'Newest class on file'
                              : `Class of ${opening.estimated_graduation_year}`}
                          </span>
                          <span>{opening.season} Season</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  </>
                  )}
                </div>
                );
              })()}

              {/* Top Recruiting States */}
              {school.top_recruiting_states && school.top_recruiting_states.length > 0 && (
                <div style={{
                  background: 'white',
                  padding: '24px',
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F9550D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                      Top Recruiting States
                    </h3>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {school.top_recruiting_states.map((state, index) => (
                      <div key={`${state.hometown_state}-${index}`} style={{
                        padding: '16px',
                        background: '#F9FAFB',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '12px' }}>
                          <div style={{
                            width: '52px',
                            height: '52px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #F9550D 0%, #DC2626 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            boxShadow: '0 2px 4px rgba(249, 85, 13, 0.2)'
                          }}>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: 'white' }}>
                              {state.hometown_state}
                            </span>
                          </div>
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: '16px', fontWeight: '700', color: '#111827', margin: '0 0 4px 0' }}>
                              {getStateName(state.hometown_state)}
                            </p>
                            <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
                              {state.player_counts} of {state.total_player_counts} players
                            </p>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '18px', fontWeight: '700', color: '#F9550D', minWidth: '50px' }}>
                            {Math.round(state.percent_of_total_players * 100)}%
                          </span>
                          <div style={{ 
                            flex: 1, 
                            height: '10px', 
                            background: '#E5E7EB', 
                            borderRadius: '5px',
                            overflow: 'hidden'
                          }}>
                            <div style={{ 
                              height: '100%', 
                              width: `${state.percent_of_total_players * 100}%`,
                              background: 'linear-gradient(90deg, #F9550D 0%, #DC2626 100%)',
                              borderRadius: '5px',
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Transfer Portal Activity */}
              <div style={{
                background: 'white',
                padding: '24px',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '1px solid #E5E7EB'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F9550D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 3h5v5"></path>
                    <path d="M8 21H3v-5"></path>
                    <path d="M21 3l-9 9"></path>
                    <path d="M3 21l9-9"></path>
                  </svg>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                    Transfer Portal Activity {school.transfer_portal_stats ? `(${school.transfer_portal_stats.year})` : ''}
                  </h3>
                </div>
                {school.transfer_portal_stats ? (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', 
                    gap: '16px'
                  }}>
                    <div style={{
                      padding: '16px',
                      background: '#DCFCE7',
                      borderRadius: '12px',
                      textAlign: 'center'
                    }}>
                      <p style={{ 
                        fontSize: '28px', 
                        fontWeight: '700', 
                        color: '#15803D', 
                        margin: '0 0 4px 0' 
                      }}>
                        {school.transfer_portal_stats.transfers_in}
                      </p>
                      <p style={{ 
                        fontSize: '13px', 
                        fontWeight: '600', 
                        color: '#166534', 
                        margin: 0,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        In
                      </p>
                    </div>
                    <div style={{
                      padding: '16px',
                      background: '#FEE2E2',
                      borderRadius: '12px',
                      textAlign: 'center'
                    }}>
                      <p style={{ 
                        fontSize: '28px', 
                        fontWeight: '700', 
                        color: '#DC2626', 
                        margin: '0 0 4px 0' 
                      }}>
                        {school.transfer_portal_stats.transfers_out}
                      </p>
                      <p style={{ 
                        fontSize: '13px', 
                        fontWeight: '600', 
                        color: '#991B1B', 
                        margin: 0,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Out
                      </p>
                    </div>
                    <div style={{
                      padding: '16px',
                      background: school.transfer_portal_stats.net >= 0 ? '#DBEAFE' : '#F3F4F6',
                      borderRadius: '12px',
                      textAlign: 'center'
                    }}>
                      <p style={{ 
                        fontSize: '28px', 
                        fontWeight: '700', 
                        color: school.transfer_portal_stats.net >= 0 ? '#1D4ED8' : '#4B5563',
                        margin: '0 0 4px 0' 
                      }}>
                        {school.transfer_portal_stats.net >= 0 ? '+' : ''}{school.transfer_portal_stats.net}
                      </p>
                      <p style={{ 
                        fontSize: '13px', 
                        fontWeight: '600', 
                        color: school.transfer_portal_stats.net >= 0 ? '#1E40AF' : '#4B5563',
                        margin: 0,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Net
                      </p>
                    </div>
                  </div>
                ) : (
                  <div style={{ 
                    padding: '24px', 
                    textAlign: 'center', 
                    background: '#F9FAFB', 
                    borderRadius: '12px' 
                  }}>
                    <p style={{ 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      color: '#9CA3AF', 
                      margin: 0 
                    }}>
                      N/A
                    </p>
                    <p style={{ 
                      fontSize: '13px', 
                      color: '#9CA3AF', 
                      margin: '8px 0 0 0' 
                    }}>
                      No transfer portal data available
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {!loading && !error && school && activeTab === 'roster' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {(() => {
                const roster = school.roster_data || [];

                return roster.length > 0 ? (
                  roster.map((player, index) => (
                    <div key={`${player.player_id}-${index}`} style={{
                      background: 'white',
                      padding: '20px',
                      borderRadius: '16px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #E5E7EB'
                    }}>
                      {/* Player Header */}
                      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                        <div style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #F9550D 0%, #DC2626 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          boxShadow: '0 2px 4px rgba(249, 85, 13, 0.2)',
                          fontSize: '20px',
                          fontWeight: '700',
                          color: 'white'
                        }}>
                          {player.jersey_number || '--'}
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: '0 0 6px 0' }}>
                            {player.player_name}
                          </h4>
                          <div style={{ display: 'flex', gap: '12px', marginBottom: '4px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#F9550D' }}>
                              {player.position_name}
                            </span>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280' }}>
                              {player.class_year_name}
                            </span>
                          </div>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0', fontWeight: '500' }}>
                            B: {player.bats || 'N/A'} / T: {player.throws || 'N/A'}
                          </p>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
                            {player.hometown}
                          </p>
                          {player.high_school && (
                            <p style={{ fontSize: '12px', color: '#9CA3AF', margin: '4px 0 0 0', fontStyle: 'italic' }}>
                              High School: {player.high_school}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Season Summary */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '12px',
                        padding: '12px',
                        background: '#F9FAFB',
                        borderRadius: '10px',
                        marginBottom: '16px'
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <p style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase' }}>
                            Games Played
                          </p>
                          <p style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                            {player.games_played}
                          </p>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <p style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase' }}>
                            Games Started
                          </p>
                          <p style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                            {player.games_started}
                          </p>
                        </div>
                      </div>

                      {/* Batting Stats */}
                      {player.at_bats > 0 && (
                        <div style={{ marginBottom: player.innings_pitched > 0 ? '16px' : 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F9550D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"></circle>
                              <line x1="12" y1="8" x2="12" y2="16"></line>
                              <line x1="8" y1="12" x2="16" y2="12"></line>
                            </svg>
                            <h5 style={{ fontSize: '14px', fontWeight: '700', color: '#111827', margin: 0 }}>
                              Batting Statistics
                            </h5>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '10px' }}>
                            <div style={{
                              textAlign: 'center',
                              padding: '10px',
                              background: '#F9FAFB',
                              borderRadius: '8px',
                              border: '1px solid #E5E7EB'
                            }}>
                              <p style={{ fontSize: '10px', color: '#6B7280', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase' }}>
                                AVG
                              </p>
                              <p style={{ fontSize: '16px', fontWeight: '700', color: '#111827', margin: 0 }}>
                                {formatBattingAvg(player.batting_average)}
                              </p>
                            </div>
                            <div style={{
                              textAlign: 'center',
                              padding: '10px',
                              background: '#F9FAFB',
                              borderRadius: '8px',
                              border: '1px solid #E5E7EB'
                            }}>
                              <p style={{ fontSize: '10px', color: '#6B7280', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase' }}>
                                OBP
                              </p>
                              <p style={{ fontSize: '16px', fontWeight: '700', color: '#111827', margin: 0 }}>
                                {formatBattingAvg(player.on_base_percentage)}
                              </p>
                            </div>
                            <div style={{
                              textAlign: 'center',
                              padding: '10px',
                              background: '#F9FAFB',
                              borderRadius: '8px',
                              border: '1px solid #E5E7EB'
                            }}>
                              <p style={{ fontSize: '10px', color: '#6B7280', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase' }}>
                                H
                              </p>
                              <p style={{ fontSize: '16px', fontWeight: '700', color: '#111827', margin: 0 }}>
                                {player.hits}
                              </p>
                            </div>
                            <div style={{
                              textAlign: 'center',
                              padding: '10px',
                              background: '#F9FAFB',
                              borderRadius: '8px',
                              border: '1px solid #E5E7EB'
                            }}>
                              <p style={{ fontSize: '10px', color: '#6B7280', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase' }}>
                                R
                              </p>
                              <p style={{ fontSize: '16px', fontWeight: '700', color: '#111827', margin: 0 }}>
                                {player.runs}
                              </p>
                            </div>
                            <div style={{
                              textAlign: 'center',
                              padding: '10px',
                              background: '#F9FAFB',
                              borderRadius: '8px',
                              border: '1px solid #E5E7EB'
                            }}>
                              <p style={{ fontSize: '10px', color: '#6B7280', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase' }}>
                                AB
                              </p>
                              <p style={{ fontSize: '16px', fontWeight: '700', color: '#111827', margin: 0 }}>
                                {player.at_bats}
                              </p>
                            </div>
                            <div style={{
                              textAlign: 'center',
                              padding: '10px',
                              background: '#F9FAFB',
                              borderRadius: '8px',
                              border: '1px solid #E5E7EB'
                            }}>
                              <p style={{ fontSize: '10px', color: '#6B7280', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase' }}>
                                FLDG
                              </p>
                              <p style={{ fontSize: '16px', fontWeight: '700', color: '#111827', margin: 0 }}>
                                {formatBattingAvg(player.fielding_percentage)}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Pitching Stats */}
                      {player.innings_pitched > 0 && (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"></circle>
                              <path d="M12 6v6l4 2"></path>
                            </svg>
                            <h5 style={{ fontSize: '14px', fontWeight: '700', color: '#111827', margin: 0 }}>
                              Pitching Statistics
                            </h5>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '10px' }}>
                            <div style={{
                              textAlign: 'center',
                              padding: '10px',
                              background: '#EFF6FF',
                              borderRadius: '8px',
                              border: '1px solid #DBEAFE'
                            }}>
                              <p style={{ fontSize: '10px', color: '#1E40AF', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase' }}>
                                ERA
                              </p>
                              <p style={{ fontSize: '16px', fontWeight: '700', color: '#3B82F6', margin: 0 }}>
                                {formatERA(player.earned_run_average)}
                              </p>
                            </div>
                            <div style={{
                              textAlign: 'center',
                              padding: '10px',
                              background: '#EFF6FF',
                              borderRadius: '8px',
                              border: '1px solid #DBEAFE'
                            }}>
                              <p style={{ fontSize: '10px', color: '#1E40AF', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase' }}>
                                IP
                              </p>
                              <p style={{ fontSize: '16px', fontWeight: '700', color: '#3B82F6', margin: 0 }}>
                                {formatInningsPitched(player.innings_pitched)}
                              </p>
                            </div>
                            <div style={{
                              textAlign: 'center',
                              padding: '10px',
                              background: '#EFF6FF',
                              borderRadius: '8px',
                              border: '1px solid #DBEAFE'
                            }}>
                              <p style={{ fontSize: '10px', color: '#1E40AF', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase' }}>
                                BF
                              </p>
                              <p style={{ fontSize: '16px', fontWeight: '700', color: '#3B82F6', margin: 0 }}>
                                {player.batters_faced}
                              </p>
                            </div>
                            <div style={{
                              textAlign: 'center',
                              padding: '10px',
                              background: '#EFF6FF',
                              borderRadius: '8px',
                              border: '1px solid #DBEAFE'
                            }}>
                              <p style={{ fontSize: '10px', color: '#1E40AF', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase' }}>
                                ER
                              </p>
                              <p style={{ fontSize: '16px', fontWeight: '700', color: '#3B82F6', margin: 0 }}>
                                {player.earned_runs}
                              </p>
                            </div>
                            <div style={{
                              textAlign: 'center',
                              padding: '10px',
                              background: '#EFF6FF',
                              borderRadius: '8px',
                              border: '1px solid #DBEAFE'
                            }}>
                              <p style={{ fontSize: '10px', color: '#1E40AF', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase' }}>
                                BB
                              </p>
                              <p style={{ fontSize: '16px', fontWeight: '700', color: '#3B82F6', margin: 0 }}>
                                {player.walks_allowed}
                              </p>
                            </div>
                            <div style={{
                              textAlign: 'center',
                              padding: '10px',
                              background: '#EFF6FF',
                              borderRadius: '8px',
                              border: '1px solid #DBEAFE'
                            }}>
                              <p style={{ fontSize: '10px', color: '#1E40AF', fontWeight: '600', margin: '0 0 4px 0', textTransform: 'uppercase' }}>
                                RA
                              </p>
                              <p style={{ fontSize: '16px', fontWeight: '700', color: '#3B82F6', margin: 0 }}>
                                {player.runs_allowed}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{
                    background: 'white',
                    padding: '64px 24px',
                    borderRadius: '16px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #E5E7EB',
                    textAlign: 'center'
                  }}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 16px' }}>
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    <p style={{ fontSize: '16px', fontWeight: '600', color: '#6B7280', margin: '0 0 8px 0' }}>
                      No roster information available
                    </p>
                    <p style={{ fontSize: '14px', color: '#9CA3AF', margin: 0 }}>
                      Roster data will be displayed when available
                    </p>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </Modal>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Unified coach-contact shell — one overlay + channel selector
          (Email / Tweet / DM). Replaces the three separate composers. */}
      {!staffMode && (
        <CoachContactModal
          isOpen={contactChannel !== null}
          initialChannel={contactChannel || 'email'}
          coach={contactCoach}
          onClose={closeContact}
        />
      )}

      {/* APS-260: report a coach change. Mounted only while open. */}
      {!staffMode && reportTarget && schoolId != null && (
        <CoachReportModal
          isOpen
          onClose={() => setReportTarget(null)}
          schoolId={schoolId}
          schoolName={school?.name}
          athleteId={athleteId || athleteProfileId || undefined}
          coach={reportTarget.coach}
          coaches={reportTarget.coaches}
          initialKind={reportTarget.kind}
          contactVisible
        />
      )}

      {/* §1.5: confirm before spending a free unlock — makes the per-coach spend
          deliberate ("1 of 3") instead of a silent tap on a channel button. */}
      {pendingUnlockCoach && (
        <div
          onClick={() => setPendingUnlockCoach(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '16px', padding: '28px', maxWidth: '400px',
              width: '100%', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%', margin: '0 auto 16px',
              background: 'rgba(249, 85, 13, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Lock size={26} color="#F9550D" />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: '0 0 8px' }}>
              Unlock {school?.name || 'this program'}?
            </h3>
            <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.5, margin: '0 0 22px' }}>
              This uses <strong style={{ color: '#111827' }}>1 of your {unlockStatus?.remaining ?? (unlockStatus?.limit ?? 3)} remaining</strong> free programs.
              You'll be able to message every coach on staff — email, tweet, and DM — as many times as you like.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setPendingUnlockCoach(null)}
                style={{
                  flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #E5E7EB',
                  background: 'white', color: '#374151', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmUnlock}
                style={{
                  flex: 1, padding: '12px', borderRadius: '10px', border: 'none',
                  background: '#F9550D', color: 'white', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
                }}
              >
                Unlock program
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SchoolProfileModal;
